/**
 * NotificationBadgeContext — web port of the RN context.
 *
 * Differences from RN:
 *  - No `expo-notifications`. The web equivalent for app-icon badges is
 *    `navigator.setAppBadge()` (Badging API), available in installed PWAs on
 *    Chromium-based browsers. We feature-detect and no-op elsewhere.
 *  - No `AppState`. We refresh on `visibilitychange` (when document becomes
 *    visible) instead.
 *  - No push-notification listeners; the page polls instead.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import inboxApi from '../api/inboxApi';
import type { BadgeCounts, NotificationCategory } from '@wg/types';
import { useAuth } from '../hooks/useAuth';
import logger from '../utils/logger';

export interface NotificationBadgeState {
  unreadCount: number;
  badgeCounts: BadgeCounts;
  loading: boolean;
  error: string | null;
  lastRefreshed: Date | null;
}

export interface NotificationBadgePreferences {
  showAppIconBadge: boolean;
  appIconCategories: NotificationCategory[];
  inboxShowDot: boolean;
}

export interface NotificationBadgeContextValue extends NotificationBadgeState {
  refresh: () => Promise<void>;
  decrementUnread: (category?: NotificationCategory) => void;
  clearAllUnread: () => void;
  preferences: NotificationBadgePreferences;
  updatePreferences: (prefs: Partial<NotificationBadgePreferences>) => void;
}

const DEFAULT_BADGE_COUNTS: BadgeCounts = {
  total: 0,
  bookings: 0,
  messages: 0,
  itinerary: 0,
  other: 0,
};

const DEFAULT_PREFERENCES: NotificationBadgePreferences = {
  showAppIconBadge: true,
  appIconCategories: ['booking', 'itinerary', 'message', 'reminder', 'trip'],
  inboxShowDot: false,
};

function setAppIconBadge(count: number): void {
  if (typeof navigator === 'undefined') return;
  const nav = navigator as Navigator & {
    setAppBadge?: (count?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  try {
    if (count > 0 && typeof nav.setAppBadge === 'function') {
      nav.setAppBadge(count).catch(() => {});
    } else if (count === 0 && typeof nav.clearAppBadge === 'function') {
      nav.clearAppBadge().catch(() => {});
    }
  } catch {}
}

const NotificationBadgeContext =
  createContext<NotificationBadgeContextValue | null>(null);

interface NotificationBadgeProviderProps {
  children: ReactNode;
  refreshInterval?: number;
}

export function NotificationBadgeProvider({
  children,
  refreshInterval = 60000,
}: NotificationBadgeProviderProps) {
  const { isAuthenticated } = useAuth();
  const isMounted = useRef(true);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [state, setState] = useState<NotificationBadgeState>({
    unreadCount: 0,
    badgeCounts: DEFAULT_BADGE_COUNTS,
    loading: false,
    error: null,
    lastRefreshed: null,
  });

  const [preferences, setPreferences] =
    useState<NotificationBadgePreferences>(DEFAULT_PREFERENCES);

  const fetchBadgeCounts = useCallback(async () => {
    if (!isAuthenticated) {
      logger.debug('[NotificationBadge] Skipping fetch - not authenticated');
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const unreadCount = await inboxApi.getUnreadCount();
      if (!isMounted.current) return;

      const badgeCounts: BadgeCounts = {
        total: unreadCount,
        bookings: 0,
        messages: 0,
        itinerary: 0,
        other: 0,
      };

      setState(prev => ({
        ...prev,
        unreadCount,
        badgeCounts,
        loading: false,
        lastRefreshed: new Date(),
      }));

      if (preferences.showAppIconBadge) setAppIconBadge(unreadCount);
    } catch (error) {
      if (!isMounted.current) return;
      const message =
        error instanceof Error ? error.message : 'Failed to fetch badge counts';
      setState(prev => ({ ...prev, loading: false, error: message }));
      logger.error('[NotificationBadge] Failed to fetch badge counts', error);
    }
  }, [isAuthenticated, preferences.showAppIconBadge]);

  const decrementUnread = useCallback(
    (category?: NotificationCategory) => {
      setState(prev => {
        const newUnread = Math.max(0, prev.unreadCount - 1);
        const newBadgeCounts = { ...prev.badgeCounts, total: newUnread };
        if (category) {
          const key = getCategoryKey(category);
          if (key && key in newBadgeCounts) {
            (newBadgeCounts as any)[key] = Math.max(
              0,
              (newBadgeCounts as any)[key] - 1,
            );
          }
        }
        if (preferences.showAppIconBadge) setAppIconBadge(newUnread);
        return { ...prev, unreadCount: newUnread, badgeCounts: newBadgeCounts };
      });
    },
    [preferences.showAppIconBadge],
  );

  const clearAllUnread = useCallback(() => {
    setState(prev => ({
      ...prev,
      unreadCount: 0,
      badgeCounts: DEFAULT_BADGE_COUNTS,
    }));
    if (preferences.showAppIconBadge) setAppIconBadge(0);
  }, [preferences.showAppIconBadge]);

  const updatePreferences = useCallback(
    (prefs: Partial<NotificationBadgePreferences>) => {
      setPreferences(prev => {
        const updated = { ...prev, ...prefs };
        if (prefs.showAppIconBadge === false) setAppIconBadge(0);
        else if (prefs.showAppIconBadge === true)
          setAppIconBadge(state.unreadCount);
        return updated;
      });
    },
    [state.unreadCount],
  );

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBadgeCounts();
    } else {
      setState({
        unreadCount: 0,
        badgeCounts: DEFAULT_BADGE_COUNTS,
        loading: false,
        error: null,
        lastRefreshed: null,
      });
      setAppIconBadge(0);
    }
  }, [isAuthenticated, fetchBadgeCounts]);

  useEffect(() => {
    if (refreshInterval > 0 && isAuthenticated) {
      refreshTimerRef.current = setInterval(fetchBadgeCounts, refreshInterval);
      return () => {
        if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      };
    }
  }, [refreshInterval, isAuthenticated, fetchBadgeCounts]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchBadgeCounts();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [isAuthenticated, fetchBadgeCounts]);

  const value: NotificationBadgeContextValue = {
    ...state,
    refresh: fetchBadgeCounts,
    decrementUnread,
    clearAllUnread,
    preferences,
    updatePreferences,
  };

  return (
    <NotificationBadgeContext.Provider value={value}>
      {children}
    </NotificationBadgeContext.Provider>
  );
}

export function useNotificationBadges(): NotificationBadgeContextValue {
  const ctx = useContext(NotificationBadgeContext);
  if (!ctx)
    throw new Error(
      'useNotificationBadges must be used within a NotificationBadgeProvider',
    );
  return ctx;
}

function getCategoryKey(
  category: NotificationCategory,
): keyof BadgeCounts | null {
  switch (category) {
    case 'booking':
      return 'bookings';
    case 'message':
      return 'messages';
    case 'itinerary':
      return 'itinerary';
    case 'promotion':
    case 'system':
    case 'reminder':
    case 'trip':
      return 'other';
    default:
      return null;
  }
}

export default NotificationBadgeContext;
